import { Card, Stack, Typography } from '@mui/material';
import { CleaningMission, User } from '@silbo/core';
import { useSuspenseQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import useLiveQuery from '../../../hooks/useLiveQuery';
import BedLocation from '../components/BedLocation';
import CleaningPage from '../components/CleaningPage';
import EmptyState from '../components/EmptyState';
import faceWink from '../assets/images/face-wink.svg';
import { useUnitOfBed } from '../hooks/useUnitOfBed';
import { getMyFinishedCleaningMissionsTodayQuery, getMyFinishedCleaningMissionsTodayQueryOptions } from '../queries';

type MyFinishedMissionsPageProps = {
  user: User;
};

const finishedAt = (mission: CleaningMission): Date | undefined =>
  mission.history.filter((entry) => entry.action === 'finished').at(-1)?.at;

const MyFinishedMissionsPage = ({ user }: MyFinishedMissionsPageProps) => {
  const { t } = useTranslation('environmental-cleaning');
  const today = new Date();
  const { data: missions } = useSuspenseQuery(getMyFinishedCleaningMissionsTodayQueryOptions(user, today));
  const unitOfBed = useUnitOfBed();
  useLiveQuery(getMyFinishedCleaningMissionsTodayQuery(user, today));
  const isEmpty = missions.length === 0;

  return (
    <CleaningPage title={t('done.title')}>
      {isEmpty && <EmptyState image={faceWink} title={t('done.empty.title')} message={t('done.empty.message')} />}
      <Stack spacing={4} padding={4}>
        <Stack spacing={2}>
          {missions.map((mission) => {
            const at = finishedAt(mission);
            return (
              <Card key={mission.id} elevation={1} sx={{ paddingX: 3, paddingY: 4 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
                  <BedLocation bed={mission.bed} unit={unitOfBed(mission.bed)} />
                  {at && <Typography variant="body2">{t('done.finishedAt', { time: format(at, 'HH:mm') })}</Typography>}
                </Stack>
              </Card>
            );
          })}
        </Stack>
      </Stack>
    </CleaningPage>
  );
};

export default MyFinishedMissionsPage;
